import { createElement, Fragment, type ReactNode } from 'react';
import {
  createUnifiedTheme,
  UnifiedThemeProvider,
  palettes,
  genPageTheme,
  shapes,
  type UnifiedTheme,
} from '@backstage/theme';
import { ThemeBlueprint } from '@backstage/plugin-app-react';
import { createFrontendModule } from '@backstage/frontend-plugin-api';

const solidBlue = genPageTheme({
  colors: ['#006ADD', '#004fa3'],
  shape: shapes.wave,
});

const pageThemeMap = {
  home: solidBlue,
  documentation: solidBlue,
  tool: solidBlue,
  service: solidBlue,
  website: solidBlue,
  library: solidBlue,
  other: solidBlue,
  app: solidBlue,
  apis: solidBlue,
  card: solidBlue,
};

const q4ComponentOverrides = {
  BackstageHeader: {
    styleOverrides: {
      header: {
        backgroundImage: 'none',
        background: '#006ADD',
      },
      title: {
        color: '#FFFFFF',
        fontSize: '24px',
        fontWeight: 'normal',
      },
    },
  },
  BackstageItemCardHeader: {
    styleOverrides: {
      root: {
        backgroundImage: 'none',
        background: '#dd276a',
      },
    },
  },
};

const q4NavigationPalette = {
  navigation: palettes.dark.navigation,
  pinSidebarButton: palettes.dark.pinSidebarButton,
};

const q4DarkTheme = createUnifiedTheme({
  palette: palettes.dark,
  pageTheme: pageThemeMap,
  components: q4ComponentOverrides,
});

const q4LightTheme = createUnifiedTheme({
  palette: {
    ...palettes.light,
    primary: { main: '#006ADD' },
    background: {
      default: '#F0F0F0',
      paper: '#FFFFFF',
    },
    ...q4NavigationPalette,
  },
  pageTheme: pageThemeMap,
  components: q4ComponentOverrides,
});

const Q4GlobalStyles = () =>
  createElement(
    'style',
    null,
    `.bui-PluginHeader { background-color: #006ADD; --bui-plugin-header-toolbar-border-bottom: none; }
     .bui-PluginHeaderToolbarName, .bui-PluginHeaderToolbarName a { color: #ffffff; }
     .bui-PluginHeaderToolbarIcon { opacity: 0.85; }`,
  );

const createThemeProvider =
  (theme: UnifiedTheme) =>
  ({ children }: { children: ReactNode }) =>
    createElement(UnifiedThemeProvider, {
      theme,
      children: createElement(
        Fragment,
        null,
        createElement(Q4GlobalStyles, null),
        children,
      ),
    });

const Q4DarkTheme = ThemeBlueprint.make({
  name: 'q4',
  params: {
    theme: {
      id: 'q4',
      title: 'Q4 Dark',
      variant: 'dark' as const,
      Provider: createThemeProvider(q4DarkTheme),
    },
  },
});

const Q4LightTheme = ThemeBlueprint.make({
  name: 'q4-light',
  params: {
    theme: {
      id: 'q4-light',
      title: 'Q4 Light',
      variant: 'light' as const,
      Provider: createThemeProvider(q4LightTheme),
    },
  },
});

export const themeModule = createFrontendModule({
  pluginId: 'app',
  extensions: [Q4DarkTheme, Q4LightTheme],
});
